/**
 * Changing where and when a basket line arrives (customer portal ordering doc 22 §7), headless: the rows a split is
 * edited as, what stops a split from being saved, and the request the page sends. The dates, prices and refusals
 * are the server's; this only holds the rows and shapes what is sent.
 */

import type {
  CustomerBasketLine,
  CustomerLineDeliveryChange,
} from "../data/customer-basket-types";

/** One address of the line and how many go there. A line to one address is one row. */
export interface DeliveryRow {
  addressId: string;
  quantity: number;
}

/** The line's own destination as rows: each address it goes to with its quantity; none where the read names no address. */
export function rowsOfLine(
  line: Pick<CustomerBasketLine, "deliveries">,
): DeliveryRow[] {
  const rows = line.deliveries.map((delivery) => ({
    addressId: delivery.address_id ?? null,
    quantity: delivery.quantity,
  }));
  return rows.every((row) => row.addressId !== null)
    ? (rows as DeliveryRow[])
    : [];
}

/** How many the rows send in all: a split line's quantity is this. */
export function rowsTotal(rows: readonly DeliveryRow[]): number {
  return rows.reduce((sum, row) => sum + row.quantity, 0);
}

/**
 * What the server would refuse about the rows (each address once, at least one to each), said before asking so Save
 * waits until it would pass. The sum needs no check: it is the line's new quantity.
 */
export function rowsProblem(rows: readonly DeliveryRow[]): string | null {
  if (new Set(rows.map((row) => row.addressId)).size < rows.length)
    return "Choose each address once.";
  if (rows.some((row) => row.quantity < 1))
    return "Send at least 1 to each address.";
  return null;
}

/**
 * The request for these rows and this date: a line of several versions sends its date alone; one row is the one
 * address (its quantity sent only where it is not the line's own); two or more rows are the split, whose sum the
 * server takes as the quantity. `speedCode` null keeps the line's date.
 */
export function changeOf(options: {
  rows: readonly DeliveryRow[];
  speedCode: string | null;
  isDateOnly: boolean;
  lineQuantity: number;
}): CustomerLineDeliveryChange {
  const { rows, speedCode, isDateOnly, lineQuantity } = options;
  const date = speedCode === null ? {} : { speed_code: speedCode };
  if (isDateOnly) return date;
  const [only] = rows;
  if (rows.length === 1 && only) {
    return {
      ...date,
      delivery_address_id: only.addressId,
      ...(only.quantity !== lineQuantity ? { quantity: only.quantity } : {}),
    };
  }
  return {
    ...date,
    delivery_lines: rows.map((row) => ({
      delivery_address_id: row.addressId,
      quantity: row.quantity,
    })),
  };
}
